import { useState, useCallback, useMemo } from 'react';

const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '\u20AC' },
  { code: 'GBP', symbol: '\u00A3' },
  { code: 'CAD', symbol: 'CA$' },
  { code: 'AUD', symbol: 'A$' },
  { code: 'JPY', symbol: '\u00A5' },
  { code: 'INR', symbol: '\u20B9' },
  { code: 'BRL', symbol: 'R$' },
  { code: 'MXN', symbol: 'MX$' },
  { code: 'CHF', symbol: 'CHF' },
];

const PAYMENT_TERMS = [
  'Due on Receipt',
  'Net 15',
  'Net 30',
  'Net 60',
  'Custom',
];

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

function createLineItem() {
  return { id: generateId(), description: '', quantity: 1, unitPrice: 0 };
}

function createBlankInvoice(invoiceNumber) {
  return {
    invoiceNumber: `INV-${String(invoiceNumber).padStart(4, '0')}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentTerms: 'Net 30',

    companyName: '',
    companyAddress: ['', ''],
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    companyLogo: null, // { dataUrl, name, width, height }
    accentColor: '#4F46E5',

    clientName: '',
    clientCompany: '',
    clientAddress: ['', ''],
    clientEmail: '',

    showShipTo: false,
    shipToName: '',
    shipToAddress: ['', ''],

    lineItems: [createLineItem()],

    showDiscount: false,
    discountType: 'percentage', // 'percentage' | 'fixed'
    discountValue: 0,

    showTax: false,
    taxRate: 0,

    showShipping: false,
    shippingLabel: 'Shipping',
    shippingAmount: 0,

    amountPaid: 0,

    notes: 'Thank you for your business!',
    paymentInstructions: '',
    termsAndConditions: '',

    currency: 'USD',
  };
}

export { CURRENCIES, PAYMENT_TERMS };

export default function useInvoice() {
  const [nextNumber, setNextNumber] = useState(2);
  const [invoice, setInvoice] = useState(() => createBlankInvoice(1));
  const [drafts, setDrafts] = useState([]);

  // Format currency using Intl
  const formatCurrency = useCallback((amount) => {
    const curr = CURRENCIES.find((c) => c.code === invoice.currency) || CURRENCIES[0];
    const decimals = curr.code === 'JPY' ? 0 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  }, [invoice.currency]);

  // Update a top-level field
  const updateField = useCallback((field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Line item operations
  const updateLineItem = useCallback((id, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  }, []);

  const addLineItem = useCallback(() => {
    setInvoice((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, createLineItem()],
    }));
  }, []);

  const removeLineItem = useCallback((id) => {
    setInvoice((prev) => ({
      ...prev,
      lineItems: prev.lineItems.length > 1
        ? prev.lineItems.filter((item) => item.id !== id)
        : prev.lineItems,
    }));
  }, []);

  const reorderLineItems = useCallback((fromIndex, toIndex) => {
    setInvoice((prev) => {
      const items = [...prev.lineItems];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...prev, lineItems: items };
    });
  }, []);

  // Logo upload
  const uploadLogo = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      updateField('companyLogo', { dataUrl: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  }, [updateField]);

  const removeLogo = useCallback(() => {
    updateField('companyLogo', null);
  }, [updateField]);

  // Calculated totals
  const totals = useMemo(() => {
    const subtotal = invoice.lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0),
      0
    );

    let discountAmount = 0;
    if (invoice.showDiscount) {
      const val = parseFloat(invoice.discountValue) || 0;
      discountAmount = invoice.discountType === 'percentage'
        ? subtotal * (val / 100)
        : val;
    }

    const afterDiscount = subtotal - discountAmount;

    let taxAmount = 0;
    if (invoice.showTax) {
      taxAmount = afterDiscount * ((parseFloat(invoice.taxRate) || 0) / 100);
    }

    const shipping = invoice.showShipping ? (parseFloat(invoice.shippingAmount) || 0) : 0;
    const total = afterDiscount + taxAmount + shipping;
    const amountPaid = parseFloat(invoice.amountPaid) || 0;
    const balanceDue = total - amountPaid;

    return { subtotal, discountAmount, taxAmount, shipping, total, amountPaid, balanceDue };
  }, [invoice]);

  // Save draft
  const saveDraft = useCallback(() => {
    setDrafts((prev) => {
      const existing = prev.findIndex((d) => d.invoiceNumber === invoice.invoiceNumber);
      const draft = { ...invoice, savedAt: Date.now() };
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = draft;
        return updated;
      }
      return [...prev, draft];
    });
  }, [invoice]);

  // New invoice
  const newInvoice = useCallback(() => {
    setInvoice(createBlankInvoice(nextNumber));
    setNextNumber((n) => n + 1);
  }, [nextNumber]);

  // Load draft
  const loadDraft = useCallback((invoiceNumber) => {
    const draft = drafts.find((d) => d.invoiceNumber === invoiceNumber);
    if (draft) {
      const { savedAt, ...data } = draft;
      setInvoice(data);
    }
  }, [drafts]);

  return {
    invoice,
    totals,
    drafts,
    formatCurrency,
    updateField,
    updateLineItem,
    addLineItem,
    removeLineItem,
    reorderLineItems,
    uploadLogo,
    removeLogo,
    saveDraft,
    newInvoice,
    loadDraft,
  };
}
